import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  getEmployees,
  createEmployee,
  deleteEmployee,
  getBranches,
  BASE_URL,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    branch: '',
    hireDate: '',
    salary: '',
    employeeNumber: '',
  });

  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  useEffect(() => {
    loadEmployees();
    if (isAdmin) {
      loadBranches();
    }
  }, [isAdmin]);

  const loadEmployees = async () => {
    try {
      const { data } = await getEmployees();
      setEmployees(data);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const { data } = await getBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error al cargar sucursales:', error);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      position: '',
      branch: '',
      hireDate: '',
      salary: '',
      employeeNumber: '',
    });
  };

  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      await createEmployee(formData);
      handleCloseDialog();
      loadEmployees();
    } catch (error) {
      console.error('Error al crear empleado:', error);
      alert(error.response?.data?.message || 'Error al crear empleado');
    }
  };

  const handleDelete = async id => {
    if (window.confirm('¿Está seguro de eliminar este empleado?')) {
      try {
        await deleteEmployee(id);
        loadEmployees();
      } catch (error) {
        console.error('Error al eliminar empleado:', error);
        alert('Error al eliminar empleado');
      }
    }
  };

  const getStatusColor = status => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'on_leave':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = status => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'inactive':
        return 'Inactivo';
      case 'on_leave':
        return 'De permiso';
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4">Empleados</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Nuevo Empleado
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Foto</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Puesto</TableCell>
              <TableCell>Nº emp.</TableCell>
              <TableCell>Sucursal</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map(employee => (
              <TableRow key={employee._id}>
                <TableCell>
                  <Avatar
                    src={
                      employee.photo
                        ? `${BASE_URL}/${employee.photo}`
                        : undefined
                    }
                    alt={`${employee.firstName} ${employee.lastName}`}
                  >
                    {employee.firstName[0]}
                    {employee.lastName[0]}
                  </Avatar>
                </TableCell>
                <TableCell>
                  {employee.firstName} {employee.lastName}
                </TableCell>
                <TableCell>{employee.email}</TableCell>
                <TableCell>{employee.position}</TableCell>
                <TableCell>{employee.employeeNumber || '—'}</TableCell>
                <TableCell>{employee.branch?.name || 'N/A'}</TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(employee.status)}
                    color={getStatusColor(employee.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/employees/${employee._id}`)}
                  >
                    <VisibilityIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(employee._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuevo Empleado</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Nombre"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Apellido"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Teléfono"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Puesto"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Número de empleado (terminal)"
              name="employeeNumber"
              value={formData.employeeNumber}
              onChange={handleChange}
              helperText="Opcional. Único. Para registrar entrada y salida en asistencia."
            />
            {isAdmin && (
              <TextField
                fullWidth
                margin="normal"
                select
                label="Sucursal"
                name="branch"
                value={formData.branch}
                onChange={handleChange}
                required
              >
                {branches.map(branch => (
                  <MenuItem key={branch._id} value={branch._id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              fullWidth
              margin="normal"
              label="Fecha de Contratación"
              name="hireDate"
              type="date"
              value={formData.hireDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Salario"
              name="salary"
              type="number"
              value={formData.salary}
              onChange={handleChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Employees;
